import { ChangeEvent, ComponentPropsWithRef, FocusEvent, forwardRef, MouseEvent, useRef, useState } from "react";
import TextAreaStyle from "./css/TextArea.module.css";
import clsx from "clsx";

type TextAreaProps = ComponentPropsWithRef<"textarea"> & {
    fullWidth?: boolean;
    minWidth?: number;
    minHeight?: number;
    gray?: boolean;
    border?: number;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    hiddenLeftIconIfFocused?: boolean;
    hiddenRightIconIfFocused?: boolean;
    hidden?: boolean;
    resize?: boolean;
};

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(({
    fullWidth = true,
    minWidth,
    minHeight = 40,
    onChange,
    onFocus,
    onBlur,
    border = 1,
    gray,
    hiddenLeftIconIfFocused = true,
    hiddenRightIconIfFocused = false,
    leftIcon,
    rightIcon,
    style,
    hidden,
    resize = false,
    ...props
}, ref) => {
    const [isOn, setIsOn] = useState(false);
    const internalRef = useRef<HTMLTextAreaElement>(null);
    const textAreaRef = (ref as React.RefObject<HTMLTextAreaElement>) || internalRef;
    
    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        onChange?.(e);
    };

    const handleFocus = (e: FocusEvent<HTMLTextAreaElement>) => {
        onFocus?.(e);
        setIsOn(true);
    };

    const handleBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
        onBlur?.(e);
        if(e.target.value === "") {
            setIsOn(false);
        }
    };

    const handleWrapperClick = (e: MouseEvent<HTMLDivElement>) => {
        textAreaRef.current?.focus();
    }

    return (
        <div className={clsx(
                TextAreaStyle.wrapper,
                fullWidth && TextAreaStyle["full-width"],
                gray && TextAreaStyle["gray"],
                !resize && TextAreaStyle["no-resize-wrapper"]
            )}
            style={{
                backgroundColor: style?.backgroundColor,
                border: border ? `${border}px solid rgba(221, 221, 221, 1)` : "none",
                '--textarea-bg': gray ? "rgba(243, 243, 243, 1)" : style?.backgroundColor,
                display: hidden ? "none" : undefined,
                minWidth: minWidth ? `${minWidth}px` : undefined,
                minHeight: minHeight ? `${minHeight + 24}px` : undefined, // padding 포함
                maxWidth: "100%",
            } as React.CSSProperties}
            onClick={handleWrapperClick}
        >
            {leftIcon && (!isOn || !hiddenLeftIconIfFocused) && <div className={TextAreaStyle.icon}>{leftIcon}</div>}
            <textarea
                ref={textAreaRef}
                className={clsx(
                    TextAreaStyle.textarea
                )}
                style={{
                    backgroundColor: gray ? "rgba(243, 243, 243, 1)" : style?.backgroundColor,
                    ...style,
                }}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                {...props}
            />
            {rightIcon && (!isOn || !hiddenRightIconIfFocused) && <div className={TextAreaStyle.icon}>{rightIcon}</div>}
        </div>
    )
});

TextArea.displayName = "TextArea";

export default TextArea;
export type {
    TextAreaProps
};

